import type { AdminCommunityComment, AdminCommunityPostDetail } from '@inflip/contracts/admin';
import { COMMUNITY_POST_CATEGORY_LABELS } from '@inflip/shared';
import { Link, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { BackLink } from '../components/back-link';
import { Button, ErrorBanner, LoadingRow, PageHeader, PromptDialog } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatDateTime } from '../lib/time-zone';

type RemoveTarget = { kind: 'post' } | { kind: 'comment'; comment: AdminCommunityComment };

/**
 * 커뮤니티 글 상세 — 본문과 모든 댓글(삭제된 것 포함)을 실제 작성자와 함께 본다.
 * 글·댓글은 사유를 적어 지울 수 있다. 사유는 작성자에게 알림으로 가고, 대기 신고는 조치함으로 닫힌다.
 */
export function CommunityPostDetailPage() {
  const { session } = useAdminAuth();
  const { postId } = useParams({ from: '/community/$postId' });
  const [detail, setDetail] = useState<AdminCommunityPostDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<RemoveTarget | null>(null);
  const [pending, setPending] = useState(false);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .getCommunityPost(session.accessToken, postId)
      .then(setDetail)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session, postId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!session) return null;
  const token = session.accessToken;

  // 실패해도 창을 닫고 다시 불러온다 — 다른 어드민이 먼저 지웠을 수 있다
  const remove = async (note: string) => {
    if (!target) return;
    setPending(true);
    setError(null);
    try {
      if (target.kind === 'post') await api.removeCommunityPost(token, postId, { note });
      else await api.removeCommunityComment(token, target.comment.id, { note });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
      setTarget(null);
      await load();
    }
  };

  const commentsById = new Map(detail?.comments.map((c) => [c.id, c]));

  return (
    <main className="w-full max-w-3xl px-6 py-8">
      <div className="mb-2">
        <BackLink
          fallback="/community"
          fallbackLabel="커뮤니티 글"
          className="text-sm font-medium text-brand hover:underline"
        />
      </div>
      <PageHeader title="커뮤니티 글 상세" />

      {error && <ErrorBanner message={error} />}
      {!detail && !error && <LoadingRow />}

      {detail && (
        <>
          <section className="rounded-lg bg-muted/40 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-muted-foreground">
                  {COMMUNITY_POST_CATEGORY_LABELS[detail.category]}
                </p>
                <h2 className="mt-0.5 break-keep text-lg font-bold">{detail.title}</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  <AuthorLink author={detail.author} /> · {formatDateTime(detail.createdAt)} · 댓글{' '}
                  {detail.commentCount} · 좋아요 {detail.likeCount}
                  {detail.pendingReportCount > 0 && (
                    <span className="font-bold text-rose-600">
                      {' '}
                      · 대기 신고 {detail.pendingReportCount}건
                    </span>
                  )}
                </p>
              </div>
              {detail.deletedAt ? (
                <span className="text-xs font-bold text-muted-foreground">
                  삭제됨 {formatDateTime(detail.deletedAt)}
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={pending}
                  onClick={() => setTarget({ kind: 'post' })}
                >
                  글 삭제
                </Button>
              )}
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed">{detail.content}</p>
          </section>

          <h3 className="mb-2 mt-8 text-sm font-bold">댓글 {detail.comments.length}개</h3>
          {detail.comments.length === 0 ? (
            <p className="text-sm text-muted-foreground">댓글이 없어요.</p>
          ) : (
            <ul className="divide-y divide-border/60">
              {detail.comments.map((c) => {
                const replyTo = c.replyToCommentId ? commentsById.get(c.replyToCommentId) : null;
                return (
                  <li key={c.id} className={`py-3 ${c.parentCommentId ? 'pl-6' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">
                          {c.parentCommentId && <span aria-hidden>↳ </span>}
                          <AuthorLink author={c.author} />
                          {c.author.id === detail.author.id && (
                            <span className="ml-1 font-semibold text-foreground">(글쓴이)</span>
                          )}{' '}
                          · {formatDateTime(c.createdAt)}
                          {c.pendingReportCount > 0 && (
                            <span className="font-bold text-rose-600">
                              {' '}
                              · 대기 신고 {c.pendingReportCount}건
                            </span>
                          )}
                        </p>
                        <p
                          className={`mt-1 whitespace-pre-wrap text-sm ${
                            c.deletedAt ? 'text-muted-foreground line-through' : ''
                          }`}
                        >
                          {replyTo && replyTo.id !== c.parentCommentId && (
                            <span className="text-brand">@{replyTo.author.name ?? '(탈퇴)'} </span>
                          )}
                          {c.content}
                          {c.isEdited && (
                            <span className="ml-1 text-xs text-muted-foreground/70">(수정됨)</span>
                          )}
                        </p>
                      </div>
                      {c.deletedAt ? (
                        <span className="shrink-0 text-xs font-bold text-muted-foreground">
                          삭제됨
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={pending}
                          onClick={() => setTarget({ kind: 'comment', comment: c })}
                        >
                          삭제
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {target && (
        <PromptDialog
          title={target.kind === 'post' ? '이 글을 삭제할까요?' : '이 댓글을 삭제할까요?'}
          description={`모든 사용자에게서 사라지고, 대기 중인 신고가 있으면 조치함으로 닫혀요. 사유는 작성자에게 알림으로 전달돼요.${
            target.kind === 'post' ? ' 글을 지워도 댓글 기록은 여기서 계속 볼 수 있어요.' : ''
          }`}
          placeholder="삭제 사유 (예: 광고·도배)"
          submitLabel="삭제"
          danger
          minLength={2}
          busy={pending}
          onSubmit={(note) => void remove(note)}
          onClose={() => setTarget(null)}
        />
      )}
    </main>
  );
}

function AuthorLink({ author }: { author: AdminCommunityComment['author'] }) {
  return (
    <Link
      to="/users/$userId"
      params={{ userId: author.id }}
      className="font-semibold text-foreground hover:underline"
    >
      {author.name ?? '(탈퇴)'}
    </Link>
  );
}
